/**
 * Plain-language vocabulary for the promo surfaces.
 *
 * Framework-free on purpose (same rule as src/prioritization/): the list, the verdict
 * page, Today and any future email all have to say the SAME words for the same
 * result, and a shop-floor employee has to understand them without a glossary.
 *
 * The engine keys (`sticky` / `snapback` / `payback` …) stay as they are — they match
 * backend/applicaite/coach/promo.py. Only what a person reads changes here.
 *
 * Naming rule learned the hard way: "Paid back" read as praise while describing a
 * LOSS. Every label below is checked against the question "could a tired manager read
 * this as the opposite of what it means?"
 *
 * LANGUAGE. Every text is read from promoVocab.json at CALL time (i18n.global.t), so a
 * reader who switches to Dutch gets Dutch from the next render; the exported objects
 * below expose their words as getters for the same reason. English is unchanged, word
 * for word. Dutch follows docs/reference/dutch_glossary.md §13.
 */
import { i18n, currentLanguage } from '@/i18n'
import { formatDate, formatEuro, formatNumber, formatWeekday } from '@/i18n/format'

const tv = (key, params) => i18n.global.t(`promoVocab.${key}`, params || {})
const isNl = () => currentLanguage() === 'nl'

/** An object whose `fields` read `promoVocab.<prefix>.<field>` when they are read. */
function texts(prefix, fields, rest = {}) {
  const o = { ...rest }
  for (const f of fields) {
    Object.defineProperty(o, f, { get: () => tv(`${prefix}.${f}`), enumerable: true })
  }
  return o
}

/**
 * TWO separate questions, never collapsed into one word.
 *
 * An earlier version had a single label covering both — so a promo that sold
 * brilliantly and then dipped read only as "Lower than normal afterwards", and the
 * during result vanished. What it did while it ran and what happened once it
 * stopped are independent facts, and both surfaces show both.
 */

/** While it ran. */
export const DURING = {
  sold_more: texts('during.sold_more', ['label'], { tone: 'good' }),
  no_change: texts('during.no_change', ['label'], { tone: 'flat' }),
  sold_less: texts('during.sold_less', ['label'], { tone: 'bad' }),
}

/**
 * The same three verdicts when there are no sales figures. Then the volume we hold is
 * ZONE TRAFFIC — people coming into the zone — and both the during and the after
 * verdict are read on it, because a sentence whose two halves are about two different
 * metrics says nothing. "Sold more" would be a claim about a till we never read, so
 * the engine names its ruler in `decay.basis` and this is the other set of words.
 */
export const DURING_TRAFFIC = {
  sold_more: texts('duringTraffic.sold_more', ['label'], { tone: 'good' }),
  no_change: texts('duringTraffic.no_change', ['label'], { tone: 'flat' }),
  sold_less: texts('duringTraffic.sold_less', ['label'], { tone: 'bad' }),
}

/**
 * The during-chip for a promo with no sales figures: zone traffic, the same metric the
 * after-chip uses. The chip is the VERDICT; which part of the funnel drove it is the
 * job of the sentence lower down the page (`attentionRead`), and conflating the two
 * was how the chip ended up saying "more people stopped" beside a paragraph saying
 * the share who stopped had not moved.
 */
export function duringFromAttention(measurement, mechanicKey = null) {
  const base = DURING_TRAFFIC[measurement?.decay?.during] || {}
  return {
    ...base,
    ...(base.label ? { label: inScope(base.label, measurement) } : {}),
    storeMoved: storeTrafficMoved(measurement),
  }
}

/**
 * Did the whole store get busier over those days? If it did, that is the honest
 * first explanation for a zone looking busier, and the figures are already divided
 * by it — but the reader deserves to be told, not to find out later.
 */
export function storeTrafficMoved(measurement) {
  // Not for a store-wide promo: there, the store getting busier is the RESULT, and
  // calling it something that "would have happened anyway" inverts the finding.
  if (measurement?.capability?.scope === 'store') return null
  const f = (measurement?.decomposition?.terms || []).find((t) => t.term === 'footfall')
  const bar = measurement?.detectability?.camera?.mdePct
  if (!f || bar === null || bar === undefined) return null
  if (Math.abs(f.deltaPct) < bar) return null
  return { deltaPct: f.deltaPct, up: f.deltaPct > 0 }
}

/**
 * The after-effect when there are no sales figures — the SAME metric as the during
 * verdict above, zone traffic, so the two chips can be read as one sentence. Never a
 * word like "attention" that appears nowhere else: the vocabulary is store traffic,
 * zone traffic, reach, stopping, conversion, and nothing besides.
 */
export const AFTER_TRAFFIC = {
  higher: texts('afterTraffic.higher', ['label', 'gloss'], {
    tone: 'good',
    soWhat: (k) => (PERSISTS.includes(k)
      ? tv('afterTraffic.higher.soWhatPersists', { worked: capitalise(mechanic(k).worked) })
      : tv('afterTraffic.higher.soWhat')),
  }),
  normal: texts('afterTraffic.normal', ['label', 'gloss'], {
    tone: 'flat',
    soWhat: () => tv('afterTraffic.normal.soWhat'),
  }),
  lower: texts('afterTraffic.lower', ['label', 'gloss'], {
    tone: 'bad',
    soWhat: () => tv('afterTraffic.lower.soWhat'),
  }),
}

/**
 * Once it stopped — measured over the weeks after it ended (four by default, longer
 * for a long promo, since a six-week promo cannot show a pull-forward dip inside
 * four). Each entry also carries what to DO about it, because "sales dipped
 * afterwards" is only worth showing if it changes the next decision.
 */
export const AFTER = {
  higher: texts('after.higher', ['label', 'gloss'], {
    tone: 'good',
    soWhat: (k) => (PERSISTS.includes(k)
      ? tv('after.higher.soWhatPersists', { worked: capitalise(mechanic(k).worked) })
      : tv('after.higher.soWhat')),
  }),
  normal: texts('after.normal', ['label', 'gloss'], {
    tone: 'flat',
    soWhat: () => tv('after.normal.soWhat'),
  }),
  lower: texts('after.lower', ['label', 'gloss'], {
    tone: 'bad',
    soWhat: () => tv('after.lower.soWhat'),
  }),
}

/**
 * How the promo behaved once it ended — the legacy single key, still used for the
 * calendar band colour and kept in step with the engine's own archetype.
 */
/** Tone -> the shared result palette (assets: .v-improved / .v-declined / .v-flat).
 *  Deliberately NOT the zone-type palette: those are saturated and mean "which zone",
 *  these are soft-tinted and mean "how did it go". Keeping the two apart is why the
 *  bands and the pills use one class set. */
function capitalise(t) {
  return t.charAt(0).toUpperCase() + t.slice(1)
}

/** Two decimals, the way a shelf price is written. */
function money(v) {
  return (Math.round(v * 100) / 100).toFixed(2)
}

/** A shelf price with its euro sign: "€7.50" / "€ 7,50". */
function euro2(v) {
  return isNl() ? formatEuro(Math.round(v * 100) / 100, { decimals: 2 }) : `€${money(v)}`
}

/** A whole-ish euro amount as toLocaleString writes it: "€1,234" / "€ 1.234". */
function euroPlain(v) {
  if (!isNl()) return `€${v.toLocaleString('en-GB')}`
  return formatEuro(v, { decimals: Number.isInteger(v) ? 0 : 2 })
}

/** A multiplier with one decimal: "3.0" / "3,0". */
function times1(v) {
  return isNl() ? formatNumber(v, { decimals: 1 }) : v.toFixed(1)
}

/** A figure exactly as it came, with the decimal mark of the language. */
function rawNum(v) {
  return isNl() ? String(v).replace('.', ',') : String(v)
}

/** "17 Oct" / "17 okt". */
function dayMonth(d) {
  return isNl()
    ? formatDate(new Date(d), { style: 'short' })
    : new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

export const TONE_CLASS = { good: 'v-improved', bad: 'v-declined', flat: 'v-flat', early: 'v-early' }

/**
 * The two facts combined into one shorthand, used only where a single chip must
 * stand alone (the calendar band). Every label names BOTH halves, so it can never
 * be read as describing only the after-effect.
 */
export const ARCHETYPE = {
  sticky: texts('archetype.sticky', ['label', 'gloss'], { tone: 'good' }),
  snapback: texts('archetype.snapback', ['label', 'gloss'], { tone: 'flat' }),
  payback: texts('archetype.payback', ['label', 'gloss'], { tone: 'bad' }),
  no_effect: texts('archetype.no_effect', ['label', 'gloss'], { tone: 'flat' }),
  backfire: texts('archetype.backfire', ['label', 'gloss'], { tone: 'bad' }),
}

export function archetype(key) {
  return ARCHETYPE[key] || null
}

const isTraffic = (basis) => basis === 'traffic'
export function during(key, basis = 'sales') {
  return (isTraffic(basis) ? DURING_TRAFFIC : DURING)[key] || null
}
export function after(key, mechanicKey = null, basis = 'sales') {
  const a = (isTraffic(basis) ? AFTER_TRAFFIC : AFTER)[key]
  if (!a) return null
  // soWhat names what the store actually logged, so it never has to say "whatever"
  return { ...a, soWhat: a.soWhat(mechanicKey) }
}
export function afterClass(key, basis = 'sales') {
  const a = (isTraffic(basis) ? AFTER_TRAFFIC : AFTER)[key]
  return a ? TONE_CLASS[a.tone] : TONE_CLASS.flat
}

export function archetypeClass(key) {
  const a = ARCHETYPE[key]
  return a ? TONE_CLASS[a.tone] : TONE_CLASS.flat
}

/** Where the promo is in its life. */
export const PHASE = texts('phase', ['planned', 'running', 'settling', 'complete'])

export function phaseLabel(p) {
  return PHASE[p] || p
}

/**
 * WHAT THE FOOTFALL SHOWED — the part that costs the store nothing to get.
 *
 * Reach and engagement come out of the nightly run whether or not anyone types in a
 * sales figure, and on their own they answer a real question: did the promo get
 * NOTICED? That is a different question from did it SELL, and worth asking
 * separately, because the fixes differ — one is about communication and placement,
 * the other about price and availability.
 *
 *   reach       of everyone in the store, how many came into this zone
 *   engagement  of those, how many stopped rather than walked past
 *
 * THE BAR. A move counts only once it clears `mdePct` — the smallest change this
 * zone's own day-to-day variance would let us distinguish from an ordinary week.
 * An earlier version used a flat 4 percentage points, which is exactly the kind of
 * picked constant this codebase forbids: 4% is nothing in a jumpy zone and a real
 * signal in a steady one.
 *
 * `coverage` never decides whether a move is readable — the variance does. It only
 * adds a caveat, because a promo on a few items makes any zone-wide move hard to
 * pin on the promo. Saying footfall "cannot show it" was a certainty we do not have.
 */
export function attentionRead({ reachPct, engagementPct, conversionPct = null,
                                mdePct = null, coverage = null, mechanicKey = null,
                                advertised = false }) {
  const what = mechanicKey ? mechanic(mechanicKey).worked : tv('thePromo')
  if (mdePct === null || mdePct === undefined) return null

  const caveat = coverage && coverage.footfallIsHardToRead
    ? tv('attention.caveat', { label: coverage.label })
    : null

  const up = (v) => v >= mdePct
  const down = (v) => v <= -mdePct
  const flat = (v) => !up(v) && !down(v)
  const r = reachPct
  const e = engagementPct
  const soldMore = conversionPct !== null && conversionPct >= mdePct
  const out = (o) => ({ ...o, caveat, bar: mdePct })

  const R = Math.round(r)
  const E = Math.round(e)
  if (up(r) && up(e)) {
    return out({
      key: 'seen_and_held',
      headline: tv('attention.seen_and_held.headline'),
      meaning: tv('attention.seen_and_held.meaning', { r: R, e: E }),
      action: advertised
        ? tv('attention.seen_and_held.actionAdvertised')
        : tv('attention.seen_and_held.action', { what }),
    })
  }
  if (up(r) && flat(e)) {
    return out({
      key: 'seen_not_held',
      // NOT "no more of them stopped": the table beside this shows stops PER DAY, and
      // those go up whenever more people come in. The share is what held steady, and
      // saying so is the difference between a reading and an apparent contradiction.
      headline: tv('attention.seen_not_held.headline'),
      meaning: tv('attention.seen_not_held.meaning', { r: R }),
      action: advertised
        ? tv('attention.seen_not_held.actionAdvertised')
        : tv('attention.seen_not_held.action', { what: capitalise(what) }),
    })
  }
  if (flat(r) && up(e)) {
    return out({
      key: 'held_not_seen',
      headline: tv('attention.held_not_seen.headline'),
      meaning: tv('attention.held_not_seen.meaning', { e: E }),
      action: advertised
        ? tv('attention.held_not_seen.actionAdvertised')
        : tv('attention.held_not_seen.action'),
    })
  }
  if (down(r) || down(e)) {
    return out({
      key: 'fell',
      headline: tv('attention.fell.headline'),
      meaning: tv('attention.fell.meaning'),
      action: tv('checkWhatElse'),
    })
  }
  return out({
    key: 'unnoticed',
    headline: tv('attention.unnoticed.headline'),
    meaning: tv('attention.unnoticed.meaning', { bar: Math.round(mdePct) }),
    action: soldMore
      ? tv('attention.unnoticed.actionSoldMore')
      // the distinction that makes the question worth asking: these two look
      // identical in the numbers and need opposite responses
      : advertised
        ? tv('attention.unnoticed.actionAdvertised')
        : tv('attention.unnoticed.action'),
  })
}

/**
 * The read for a promo on the whole store. One question: did more people come in?
 * Everything else a store-wide promo did shows up in the money, not in the cameras.
 */
function storeRead(measurement, foot, mechanicKey) {
  const bar = measurement?.detectability?.camera?.mdePct
  if (!foot || bar === null || bar === undefined) return null
  const f = foot.deltaPct
  const what = mechanicKey ? mechanic(mechanicKey).worked : tv('thePromo')
  const common = { caveat: null, bar, storeWide: true }

  if (f >= bar) {
    return {
      ...common,
      key: 'store_busier',
      headline: tv('store.busier.headline'),
      meaning: tv('store.busier.meaning', { f: Math.round(f) }),
      action: tv('store.busier.action', { what: capitalise(what) }),
    }
  }
  if (f <= -bar) {
    return {
      ...common,
      key: 'store_quieter',
      headline: tv('store.quieter.headline'),
      meaning: tv('store.quieter.meaning', { f: Math.round(Math.abs(f)) }),
      action: tv('store.quieter.action'),
    }
  }
  return {
    ...common,
    key: 'store_same',
    headline: tv('store.same.headline'),
    meaning: tv('store.same.meaning', { bar: Math.round(bar) }),
    action: tv('store.same.action', { what: capitalise(what) }),
  }
}

/**
 * The attention read for a zone that cannot see stopping. It answers the one question
 * a counting line can: did the promo bring more people to this part of the store? It
 * must never imply the other half was zero — only that it was never measured.
 */
function reachOnlyRead(measurement, reach, mechanicKey) {
  const bar = measurement?.detectability?.camera?.mdePct
  if (bar === null || bar === undefined) return null
  const r = reach.deltaPct
  const what = mechanicKey ? mechanic(mechanicKey).worked : tv('thePromo')
  const common = { caveat: null, bar, countsOnly: true }

  if (r >= bar) {
    return {
      ...common,
      key: 'came_more',
      headline: tv('reachOnly.more.headline'),
      meaning: tv('reachOnly.more.meaning', { r: Math.round(r) }),
      action: tv('reachOnly.more.action', { what: capitalise(what) }),
    }
  }
  if (r <= -bar) {
    return {
      ...common,
      key: 'came_fewer',
      headline: tv('reachOnly.fewer.headline'),
      meaning: tv('reachOnly.fewer.meaning', { r: Math.round(Math.abs(r)) }),
      action: tv('checkWhatElse'),
    }
  }
  return {
    ...common,
    key: 'came_same',
    headline: tv('reachOnly.same.headline'),
    meaning: tv('reachOnly.same.meaning', { bar: Math.round(bar) }),
    action: tv('reachOnly.same.action'),
  }
}

/** Pull the moves and the bar out of a measurement. */
export function attentionFrom(measurement, mechanicKey = null) {
  const t = (k) => (measurement?.decomposition?.terms || []).find((x) => x.term === k)
  // Store-wide: the question is not which part of the store noticed, it is whether
  // more people came through the door at all.
  if (measurement?.capability?.scope === 'store') {
    return storeRead(measurement, t('footfall'), mechanicKey)
  }
  const reach = t('reach')
  const eng = t('engagement')
  // A counting line has no engagement term at all — not a zero, an absence — so the
  // read speaks only to what it can see: how many people the promo brought in.
  if (reach && !eng && measurement?.capability?.engagementMeasurable === false) {
    return reachOnlyRead(measurement, reach, mechanicKey)
  }
  if (!reach || !eng) return null
  const conv = t('conversion')
  return attentionRead({
    reachPct: reach.deltaPct,
    engagementPct: eng.deltaPct,
    conversionPct: conv ? conv.deltaPct : null,
    mdePct: measurement?.detectability?.camera?.mdePct ?? null,
    coverage: measurement?.coverage || null,
    mechanicKey,
    advertised: !!measurement?.advertised,
  })
}

/**
 * The break-even, said in money rather than as a bare multiplier.
 *
 * "You need 3x as many" means nothing on its own. "At 30% off you keep EUR 7.50 a unit
 * instead of EUR 22.50, so you need 3x the sales to make the same money" is the same
 * fact, and a shop manager can check it against their own prices.
 */
export function breakEvenLine(margin) {
  if (!margin) return null
  if (margin.impossible) {
    return tv('breakEven.impossible', { discount: discountPhrase(margin) })
  }
  if (margin.breakEvenLift === null || margin.breakEvenLift === undefined) return null
  if (!margin.discountPctMax) {
    return tv('breakEven.nothingOff')
  }
  const avg = margin.uniformDiscount === false ? tv('onAverage') : ''
  return tv('breakEven.line', {
    discount: discountPhrase(margin),
    promo: euro2(margin.unitMarginOnPromo),
    avg,
    normal: euro2(margin.unitMarginNormal),
    x: times1(margin.breakEvenLift + 1),
  })
}

/**
 * What the store told us the promo WAS. Every sentence explaining why something
 * worked traces back to one of these — we never see a display, a flyer or a price
 * tag, only what was logged.
 */
const MECH_FIELDS = ['name', 'label', 'did', 'worked']
export const MECHANIC = {
  pct_off: texts('mechanic.pct_off', MECH_FIELDS),
  bogo: texts('mechanic.bogo', MECH_FIELDS),
  bundle: texts('mechanic.bundle', MECH_FIELDS),
  price_cut: texts('mechanic.price_cut', MECH_FIELDS),
  display_move: texts('mechanic.display_move', MECH_FIELDS),
  demo: texts('mechanic.demo', MECH_FIELDS),
  advertised: texts('mechanic.advertised', MECH_FIELDS),
  // Store-wide levers. Not price levers at all: the margin per sale is untouched and
  // the cost is what you bought, so they live or die on whether people came in.
  gift: texts('mechanic.gift', MECH_FIELDS),
  store_event: texts('mechanic.store_event', MECH_FIELDS),
  loyalty: texts('mechanic.loyalty', MECH_FIELDS),
  other: texts('mechanic.other', MECH_FIELDS),
}

/** Levers that cost you money without touching the price of anything. */
export const NO_PRICE_MECHANICS = ['gift', 'store_event', 'loyalty', 'display_move', 'demo']

/**
 * Mechanics that hand the customer something instead of taking money off, so a
 * percentage is not a thing they can have. Distinct from NO_PRICE_MECHANICS, which
 * only says what the DEFAULT discount is: a demo or a display move takes nothing off
 * by default, but either can be run with a price cut, and saying so is correct.
 */
export const NO_DISCOUNT_MECHANICS = ['gift', 'store_event', 'loyalty']

/**
 * Mechanics whose change is still THERE after the promo window closes. A feature
 * display can be left where it is; a price cut, a 2 for 1 or an advertised week are
 * over when they are over. It changes what "it kept selling afterwards" means you
 * should DO, and getting it wrong told a store to leave a discount in place that had
 * already ended.
 */
const PERSISTS = ['display_move']

export function mechanic(key) {
  return MECHANIC[key] || MECHANIC.other
}

/**
 * A promo with 0% off confuses people — the word "promotion" implies the customer
 * paid less. So say what DID change and what did not, in the customer's terms,
 * before drawing any conclusion from it.
 */
export function noDiscountLine(mechanicKey) {
  return tv('noDiscount.line', {
    label: mechanic(mechanicKey).label, worked: mechanic(mechanicKey).worked })
}

/** Short form of the same point, for a card. */
export function noDiscountShort(mechanicKey) {
  return tv('noDiscount.short', { label: mechanic(mechanicKey).label })
}

/**
 * The whole result in one plain sentence: what you changed, what it sold, what
 * happened after, what it made.
 *
 * Written to be read cold by someone on the shop floor. No "lift", no "outlasted",
 * no "incremental", and nothing about how we know what we know — a manager wants the
 * answer, not our epistemics. The 0%-off case is stated as what the customer paid,
 * because the word "promotion" implies a price cut and that confusion kept recurring.
 */
/**
 * What happened, as a sentence rather than two pills.
 *
 * Pills are a scanning device: they earn their place on a list of cards you are
 * skimming. On a page you are reading, an uppercase chip dropped into a line of
 * prose cannot sit on the same baseline as the words around it, which is why that
 * row never aligned. A sentence aligns by definition — the colour does the same job
 * the pill did, on the two phrases that carry the result.
 *
 * Returned as segments so the template can colour the phrases without any HTML in
 * the string.
 */
/**
 * The same sentence, said about the store when the store IS the scope. The traffic
 * vocabulary is written for a zone, and store-wide it read "it drew more people into
 * the zone" about a promo whose zone is the whole shop. A presentation swap only —
 * the figure behind it is the same store footfall either way.
 */
export const inScope = (text, measurement) => (
  measurement?.capability?.scope === 'store'
    ? (isNl()
      ? String(text).replace(/\bde zone\b/g, 'de winkel')
      : String(text).replace(/\bthe zone\b/g, 'the store'))
    : text)

export function outcomeSegments(measurement) {
  return _outcomeSegments(measurement)?.map(
    (seg) => ({ ...seg, t: inScope(seg.t, measurement) })) || null
}

function _outcomeSegments(measurement) {
  const d = measurement?.decay?.during
  const a = measurement?.decay?.after
  const weeks = measurement?.windows?.post?.weeks || 4
  if (!d) return null

  // No sales figures means the verdict came off the cameras. Three things could have
  // moved there — the store's footfall, the share of it reaching this zone, the share
  // of those who stopped — so the clause is taken from the same funnel read the chip
  // and the paragraph use, rather than always naming the last of the three.
  const stops = measurement?.decay?.basis === 'traffic'
  const TONES = { sold_more: 'good', no_change: 'flat', sold_less: 'bad' }
  const ruler = stops ? 'traffic' : 'sales'
  const DUR = TONES[d]
    ? { text: tv(`outcome.${ruler}.${d}`), tone: TONES[d],
        tail: tv(d === 'no_change' ? 'outcome.tailAs' : 'outcome.tailThan') }
    : null
  if (!DUR) return null

  // Whichever way it reads, a store that got busier over the same days is the first
  // honest explanation for a busier zone. It is already divided out of the figures;
  // this is so the reader is told rather than finding out later.
  const store = storeTrafficMoved(measurement)
  const storeTail = store
    ? [{ t: tv('outcome.storeTail', {
      pct: Math.round(Math.abs(store.deltaPct)),
      word: tv(store.up ? 'busier' : 'quieter') }) }]
    : []

  // 2) a promo still running has not finished doing whatever it is doing; calling it
  //    "about the same" four days in states a result we do not have
  if (measurement?.phase === 'running') {
    return [{ t: tv('outcome.stillRunning') }]
  }

  // a promo that did not move while it ran has no after-effect worth a clause
  if (d !== 'sold_more' || DUR.tone === 'flat' || DUR.tone === 'bad') {
    return [{ t: tv('outcome.it') }, { t: DUR.text, tone: DUR.tone }, { t: DUR.tail },
            ...storeTail]
  }

  const AFTER_TONES = { higher: 'good', normal: 'flat', lower: 'bad' }
  const AFT = AFTER_TONES[a]
    ? {
      text: tv(`outcome.after.${ruler}.${a}`),
      tone: AFTER_TONES[a],
      join: tv(`outcome.after.join.${a}`),
      tail: tv(a === 'normal' ? 'outcome.after.tailWithin' : 'outcome.after.tailFor', { weeks }),
    }
    : null

  if (!AFT) {
    return [{ t: tv('outcome.it') }, { t: DUR.text, tone: DUR.tone },
            { t: tv('outcome.afterNotIn') }, ...storeTail]
  }
  return [
    { t: tv('outcome.it') }, { t: DUR.text, tone: DUR.tone },
    { t: tv('outcome.whileRan', { join: AFT.join }) }, { t: AFT.text, tone: AFT.tone },
    { t: AFT.tail },
    ...storeTail,
  ]
}

/**
 * The promo's setup, as three facts rather than a sentence.
 *
 * "You moved the display and took nothing off the price, so customers paid the usual
 * and every extra sale kept its full margin" is three structured values wearing a
 * paragraph. As facts they scan in a second, align with the money band below, and
 * make the per-item contrast visual — EUR 7.50 sitting under "was EUR 22.50" says
 * more about why a promo lost money than any clause can.
 */
export function setupFacts(measurement, mechanicKey) {
  const mg = measurement?.margin || {}
  const off = mg.discountPct || 0
  const facts = [
    { value: mechanic(mechanicKey).name, label: tv('facts.whatYouDid') },
    {
      value: off ? tv('pctOff', { n: Math.round(off * 100) }) : tv('facts.noPriceChange'),
      label: tv('facts.forTheCustomer'),
      sub: off ? null : tv('facts.paidTheUsual'),
    },
  ]
  if (mg.unitMarginOnPromo !== undefined && mg.unitMarginOnPromo !== null) {
    facts.push({
      value: euro2(mg.unitMarginOnPromo),
      label: tv('facts.keptPerItem'),
      sub: off ? tv('facts.was', { eur: euro2(mg.unitMarginNormal) }) : tv('facts.usualMargin'),
      tone: off ? 'bad' : 'good',
    })
  }
  return facts
}

/**
 * What you changed and what it did to each sale. This is the one part of the result
 * that does not appear anywhere else on the detail page — the figures, the pills and
 * the counts all show the rest — so it is the only line that page keeps.
 */
/** "30% off" when every item carried the same cut, "30–35% off" when they did not. */
export function discountPhrase(margin) {
  const lo = margin?.discountPctMin
  const hi = margin?.discountPctMax
  if (lo == null) return null
  return lo === hi ? tv('pctOff', { n: lo }) : tv('pctOffRange', { lo, hi })
}

/**
 * What you DID — the setup, not the result.
 *
 * It is built from what was typed on the form: the mechanic, the discount, whether it
 * was announced. None of that depends on sales figures, so none of it may be withheld
 * for want of them. An earlier version returned null whenever `margin.confidence` was
 * `"na"`, which blanked "What you did" on every promo with no till figures — a promo
 * where we certainly know what was done, because somebody logged it.
 *
 * The one clause that DOES need the sales side is the money per sale, so that clause
 * alone is conditional. `data` is the promo's own `Data` blob, used as the fallback
 * for the discount when there is no margin block to derive it from.
 */
export function changeLine(measurement, data) {
  const mg = measurement?.margin || {}
  const d = typeof data === 'string' ? { mechanic: data } : (data || {})
  const ad = measurement?.advertised || d.advertised ? tv('change.andAdvertised') : ''

  // A mechanic that GIVES something has no percentage off to read, whatever sits on
  // the record: a gift promo was saved carrying discountPct 30 — the form's default,
  // never shown to anyone and never edited — and the page dutifully announced "a gift
  // at 30% off". Narrower than NO_PRICE_MECHANICS on purpose: a demo or a moved
  // display can perfectly well run a price cut alongside it.
  const noPrice = NO_DISCOUNT_MECHANICS.includes(d.mechanic)
  // the discount as derived from the entered figures, else as it was typed, else from
  // the item list — whichever we actually have. A zero is NOT a discount: a display
  // move at full price is a promotion, and saying "you took 0% off" of it is absurd.
  let phrase = (!noPrice && mg.discountPctMax) ? discountPhrase(mg) : null
  if (!phrase && !noPrice) {
    const typed = [...new Set((d.items || [])
      .map((i) => Math.round(Number(i?.discountPct) || 0))
      .filter((n) => n > 0))].sort((a, b) => a - b)
    if (typed.length) {
      phrase = typed[0] === typed[typed.length - 1]
        ? tv('pctOff', { n: typed[0] })
        : tv('pctOffRange', { lo: typed[0], hi: typed[typed.length - 1] })
    } else if (Number(d.discountPct) > 0) {
      phrase = tv('pctOff', { n: Math.round(Number(d.discountPct)) })
    }
  }

  if (!phrase) {
    const said = tv('change.youDid', { did: mechanic(d.mechanic).did, ad })
    // "...and advertised it and took nothing off the price" is two ands in one
    // breath; with an advertising clause the price point gets its own sentence.
    return ad
      ? tv('change.nothingOffAdvertised', { said })
      : tv('change.nothingOff', { said })
  }

  // A price cut IS the discount, so naming both would repeat itself. Anything else —
  // a bundle, a 2 for 1, a demo — is a fact the discount does not carry, and dropping
  // it left "Back-to-work bundle" described as "you took 15% off".
  const PRICE_ONLY = ['pct_off', 'price_cut', 'advertised', 'other']
  const open = PRICE_ONLY.includes(d.mechanic)
    ? tv('change.tookPhrase', { phrase })
    : tv('change.didAt', { did: mechanic(d.mechanic).did, phrase })

  // the money per sale is the only part that needs the till
  const hasMoney = mg.confidence && mg.confidence !== 'na'
    && mg.unitMarginOnPromo != null && mg.unitMarginNormal != null
  if (!hasMoney) return tv('change.plain', { open, ad })

  // several items at different prices: the figures are an average across them
  const avg = mg.uniformDiscount === false ? tv('onAverage') : ''
  return tv('change.withMoney', {
    open, ad, avg, promo: euro2(mg.unitMarginOnPromo), normal: euro2(mg.unitMarginNormal) })
}

/**
 * The whole result in one plain sentence: what you changed, what it sold, what
 * happened after, and (on a surface that is not already showing it) what it made.
 *
 * Written to be read cold by someone on the shop floor. No "lift", no "outlasted",
 * no "incremental", and nothing about how we know what we know — a manager wants the
 * answer, not our epistemics. The 0%-off case is stated as what the customer paid,
 * because the word "promotion" implies a price cut and that confusion kept recurring.
 *
 * `withTotal` is false where the euro is already the headline beside it, so the
 * sentence does not repeat a number the reader is looking straight at.
 */
export function moneySentence(measurement, mechanicKey, { withTotal = true } = {}) {
  const mm = measurement || {}
  if (mm.phase === 'running') {
    return tv('money.running')
  }
  const u = mm.promotedSales?.promoUnitsPerDay
  const mg = mm.margin || {}
  if (!u || mg.confidence === 'na') {
    return tv('money.noSales')
  }

  const sold = tv('money.sold', { after: rawNum(u.after), before: rawNum(u.before) })
  const weeks = mm.windows?.post?.weeks || 4
  const afterBit = ['higher', 'normal', 'lower'].includes(mm.decay?.after)
    ? tv(`money.after.${mm.decay.after}`, { weeks }) : ''

  let tail = ''
  if (withTotal) {
    const made = euroPlain(Math.abs(mg.eur))
    tail = ` ${tv(mg.eur >= 0 ? 'madeYou' : 'costYou', { eur: made })}.`
  }
  const shortfall = (mg.eur < 0 && mg.breakEvenLift)
    ? tv('money.shortfall', { x: times1(mg.breakEvenLift + 1) })
    : ''

  return `${changeLine(mm, mechanicKey)} ${sold}${afterBit ? ', ' + afterBit : ''}.`
    + `${tail}${shortfall}`
}

/**
 * WOULD YOU RUN IT AGAIN — the one thing the figures cannot answer.
 *
 * This replaced "It worked — do it again", which asked the reader to restate what
 * the page had just told them: the euros and the after-effect already say whether it
 * worked. What they cannot say is WHY, or what you would do differently — it worked
 * but only because that fortnight was freakishly warm; it flopped because you ran
 * out of stock on day three and the offer was fine.
 *
 * And it only earns its place because something reads it: the next time a promo is
 * logged in this zone, the form shows what you said here. Collecting a judgement
 * with no consumer is how the old version ended up dead.
 */
export const REPEAT = {
  as_was: texts('repeat.as_was', ['label', 'chip'], { tone: 'good' }),
  with_changes: texts('repeat.with_changes', ['label', 'chip'], { tone: 'good' }),
  no: texts('repeat.no', ['label', 'chip'], { tone: 'bad' }),
  too_early: texts('repeat.too_early', ['label', 'chip'], { tone: 'flat' }),
}

export function repeatAnswer(key) {
  return REPEAT[key] || null
}

export function repeatClass(key) {
  const r = REPEAT[key]
  return r ? TONE_CLASS[r.tone] : TONE_CLASS.flat
}

/**
 * What the form shows when you pick a zone you have promoted in before — the moment
 * the judgement above is actually worth something, because it is the moment you are
 * deciding whether to do it again.
 */
export function priorPromoPrompt(promo) {
  if (!promo) return null
  const r = repeatAnswer(promo?.Data?.outcome?.repeat)
  if (!r) return null
  const eur = promo.measurement?.margin?.eur
  const made = eur == null ? null
    : tv(eur < 0 ? 'costYou' : 'madeYou', { eur: euroPlain(Math.abs(eur)) })
  return {
    promoId: promo.PromoId,
    name: promo.Data.name,
    tone: r.tone,
    line: tv('prior', {
      label: mechanic(promo.Data.mechanic).label, zone: promo.Data.zoneName,
      name: promo.Data.name, answer: r.label.toLowerCase() })
      + (made ? ` ${made}.` : ''),
    note: promo.Data.outcome?.note || null,
  }
}

/**
 * The three stretches a promo is measured over — mirrors coach/promo._comparison_shape
 * so the days we ASK for are exactly the days the calculation will use. If these two
 * ever drifted apart, somebody would total the wrong week and never know.
 *
 *   <= 7 days  -> the same weekdays, 4 blocks either side
 *   >= 8 days  -> one continuous stretch of whole weeks
 */
const MATCHED_WEEKDAY_MAX = 7
const MATCHED_BLOCKS = 4
// The baseline and the after-period are the SAME length. Mirrors
// coach/promo._comparison_shape: four weeks at least, whole weeks beyond that.
const WINDOW_MIN_DAYS = 28
const DAY = 86400000

function shape(start, end, forward) {
  const days = Math.round((end - start) / DAY) + 1
  const sign = forward ? 1 : -1
  if (days <= MATCHED_WEEKDAY_MAX) {
    const out = []
    for (let b = 1; b <= MATCHED_BLOCKS; b++) {
      for (let i = 0; i < days; i++) {
        out.push(new Date(start.getTime() + i * DAY + sign * 7 * b * DAY))
      }
    }
    out.sort((a, b2) => a - b2)
    return { mode: 'matched-weekday', dates: out }
  }
  const weeks = Math.ceil(days / 7) * 7
  const len = Math.max(WINDOW_MIN_DAYS, weeks)
  const first = forward
    ? new Date(end.getTime() + DAY)
    : new Date(start.getTime() - len * DAY)
  const out = []
  for (let i = 0; i < len; i++) out.push(new Date(first.getTime() + i * DAY))
  return { mode: 'contiguous', dates: out }
}

export function comparisonWindows(startIso, endIso) {
  if (!startIso || !endIso) return null
  const start = new Date(startIso)
  const end = new Date(endIso)
  if (!(end >= start)) return null
  const before = shape(start, end, false)
  const after = shape(start, end, true)
  const during = []
  for (let d = new Date(start); d <= end; d = new Date(d.getTime() + DAY)) during.push(d)
  const iso = (d) => d.toISOString().slice(0, 10)
  const pack = (o) => ({
    mode: o.mode,
    days: o.dates.length,
    dates: o.dates.map(iso),
    start: iso(o.dates[0]),
    end: iso(o.dates[o.dates.length - 1]),
  })
  return {
    mode: before.mode,
    before: pack(before),
    during: pack({ mode: 'contiguous', dates: during }),
    after: pack(after),
  }
}

/**
 * How to describe one of those stretches to a person. A continuous block is a range;
 * scattered same-weekday blocks have to be listed, because "27 Apr – 25 May" would
 * have them totalling five weeks instead of four Saturdays.
 */
export function describeWindow(win) {
  if (!win) return null
  const fmt = dayMonth
  if (win.mode === 'matched-weekday' && win.dates?.length) {
    const weekday = isNl()
      ? formatWeekday(new Date(win.dates[0]))
      : new Date(win.dates[0]).toLocaleDateString('en-GB', { weekday: 'long' })
    const perBlock = win.days / MATCHED_BLOCKS
    if (perBlock === 1) {
      // every Dutch weekday ends in "dag": maandag -> maandagen
      return tv('window.weekdays', {
        weekdays: isNl() ? `${weekday}en` : `${weekday}s`, dates: win.dates.map(fmt).join(', ') })
    }
    return tv('window.sameDays', { n: perBlock, from: fmt(win.start), to: fmt(win.end) })
  }
  return `${fmt(win.start)} – ${fmt(win.end)}`
}

/**
 * Which of the three sales figures can be filled in TODAY, decided by the dates on
 * the promo and nothing else.
 *
 * NOTHING IS ASKED UNTIL THE PROMO HAS FINISHED. The stretch before it is technically
 * knowable from the day it starts, but nobody pulls a till report twice: they run the
 * promo, then sit down once and type all of it. Showing one lone row on a promo that
 * is still running is friction at the wrong moment, and showing three rows where two
 * are greyed out reads as broken.
 *
 * Once it has finished, the rows open as each stretch closes:
 *
 *   before, during  -> the day the promo ends
 *   after           -> N weeks after that
 *
 * `null` means do not ask yet — no dates, or nothing finished. The caller shows
 * `salesRowsNote` instead, which says what will be wanted and when.
 */
export function salesRows(startIso, endIso, todayIso) {
  const w = comparisonWindows(startIso, endIso)
  if (!w) return null
  const today = todayIso || new Date().toISOString().slice(0, 10)
  if (w.during.end >= today) return null
  const row = (key, label, win, over, waiting) => ({
    key,
    label,
    pending: !over,
    when: over ? describeWindow(win) : waiting,
  })
  return [
    row('before', tv('rows.before'), w.before, true),
    row('during', tv('rows.during'), w.during, true),
    // name the day it becomes fillable: "come back when they are" makes the reader
    // work out a date we already know
    row('after', tv('rows.after'), w.after, w.after.end < today,
        tv('rows.readyOn', { day: dayAfter(w.after.end) })),
  ]
}

/** "7 Oct" for the day after an ISO date — when a window that ends then is readable. */
function dayAfter(iso) {
  const d = new Date(iso)
  d.setUTCDate(d.getUTCDate() + 1)
  return dayMonth(d)
}

/** What to say when there is nothing to ask for yet, given the same dates. */
export function salesRowsNote(startIso, endIso, todayIso) {
  // No dates, nothing to say: the two date fields directly above are the instruction.
  if (!startIso || !endIso) return null
  const today = todayIso || new Date().toISOString().slice(0, 10)
  const w = comparisonWindows(startIso, endIso)
  if (w && w.during.end >= today) {
    // One fact the dates do not already give them: when the ask arrives.
    const ends = dayMonth(w.during.end)
    return tv('rows.askOn', { day: ends })
  }
  return null
}

/** Signed euro, using a real minus sign rather than a hyphen. */
export function eur(v) {
  if (v === null || v === undefined) return '—'
  const n = Math.abs(Math.round(v))
  return `${v < 0 ? '−' : '+'}${isNl() ? formatEuro(n) : `€${n.toLocaleString('en-GB')}`}`
}

export function fmtRange(a, b) {
  if (a === b) return dayMonth(a)
  return `${dayMonth(a)} – ${dayMonth(b)}`
}

/**
 * What every finished promo added up to — the promo answer to the Actions tab's
 * "tracked revenue impact to date".
 *
 * Actions reports a €/month RUN RATE because an action is a permanent change that
 * keeps paying. A promo is a one-off event, so the honest figure is a CUMULATIVE
 * total: what your promotions have earned or cost you so far. Running and
 * just-finished promos are left out on purpose — the same rule Actions uses for
 * anything not yet matured.
 */
export function rollup(promos) {
  const done = (promos || []).filter((p) => p.measurement?.phase === 'complete'
    && p.measurement?.margin?.confidence === 'good')
  if (!done.length) return null
  let total = 0
  let wins = 0
  let losses = 0
  for (const p of done) {
    const e = p.measurement.margin.eur || 0
    total += e
    if (e > 0) wins++
    else if (e < 0) losses++
  }
  return { total, wins, losses, count: done.length }
}
